import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import { createFocusTrap, FocusTrap } from "focus-trap";
import { activeModal, approveModal, cancelModal } from "../store/modal-store";

/**
 * Base modal component with accessibility features.
 * All modals should extend this class for consistent behavior.
 *
 * Features:
 * - Light DOM (Tailwind styles work directly)
 * - Focus trap (keyboard navigation contained within modal)
 * - Escape key to close
 * - Click outside to close (optional)
 * - Body scroll lock when open
 * - Previous focus restoration
 *
 * Usage:
 * 1. Extend this class
 * 2. Set the `name` property to match your modal registration
 * 3. Override `render()` to provide custom content
 * 4. Call `handleApprove()` or `handleCancel()` from your buttons
 */
@customElement("modal-base")
export class ModalBase extends LitElement {
  /** Modal name (must match registration in modal-store) */
  @property() name = "";

  /** Allow closing by clicking outside the modal */
  @property({ type: Boolean }) allowOutsideClick = true;

  /** Show close button in header */
  @property({ type: Boolean }) showCloseButton = true;

  @state() protected _isOpen = false;

  private _focusTrap: FocusTrap | null = null;
  private _previouslyFocused: HTMLElement | null = null;

  constructor() {
    super();

    // Subscribe to activeModal signal
    effect(() => {
      const shouldBeOpen = activeModal.value === this.name;

      if (shouldBeOpen !== this._isOpen) {
        this._isOpen = shouldBeOpen;

        if (this._isOpen) {
          this._onOpen();
        } else {
          this._onClose();
        }
      }
    });
  }

  // Light DOM — no shadow root (allows Tailwind to work)
  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this._setupEventListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._cleanupEventListeners();
    this._deactivateFocusTrap();
  }

  // ─── Lifecycle Hooks ──────────────────────────────────────────────────────

  private _onOpen() {
    // Remember what was focused before opening
    this._previouslyFocused = document.activeElement as HTMLElement;

    // Lock body scroll
    document.body.style.overflow = "hidden";

    // Wait for DOM to update, then activate focus trap
    this.updateComplete.then(() => {
      this._activateFocusTrap();
    });
  }

  private _onClose() {
    // Restore body scroll
    document.body.style.overflow = "";

    // Deactivate focus trap
    this._deactivateFocusTrap();

    // Restore previous focus
    if (
      this._previouslyFocused &&
      typeof this._previouslyFocused.focus === "function"
    ) {
      this._previouslyFocused.focus();
    }
  }

  // ─── Focus Trap ───────────────────────────────────────────────────────────

  private _activateFocusTrap() {
    if (this._focusTrap) {
      this._focusTrap.activate();
      return;
    }

    const modalElement = this.querySelector(".modal-dialog") as HTMLElement;
    if (!modalElement) {
      console.warn("[modal-base] No .modal-dialog element found for focus trap");
      return;
    }

    try {
      this._focusTrap = createFocusTrap(modalElement, {
        initialFocus: () => {
          // Try to focus first input, otherwise first focusable element
          const firstInput = modalElement.querySelector(
            "input, textarea, select"
          ) as HTMLElement;
          if (firstInput) return firstInput;

          const firstFocusable = modalElement.querySelector(
            'button, [href], [tabindex]:not([tabindex="-1"])'
          ) as HTMLElement;
          return firstFocusable || modalElement;
        },
        escapeDeactivates: true,
        allowOutsideClick: this.allowOutsideClick,
        onDeactivate: () => {
          // Escape key was pressed
          this.handleCancel();
        },
      });

      this._focusTrap.activate();
    } catch (error) {
      console.error("[modal-base] Error creating focus trap:", error);
    }
  }

  private _deactivateFocusTrap() {
    if (this._focusTrap) {
      this._focusTrap.deactivate();
      this._focusTrap = null;
    }
  }

  // ─── Event Handlers ───────────────────────────────────────────────────────

  private _handleKeydown: ((e: KeyboardEvent) => void) | null = null;

  private _setupEventListeners() {
    this._handleKeydown = (e: KeyboardEvent) => {
      if (!this._isOpen) return;

      // Escape key handled by focus trap, but we double-check here
      if (e.key === "Escape") {
        e.preventDefault();
        this.handleCancel();
      }
    };
    document.addEventListener("keydown", this._handleKeydown);
  }

  private _cleanupEventListeners() {
    if (this._handleKeydown) {
      document.removeEventListener("keydown", this._handleKeydown);
    }
  }

  private _handleOverlayClick(e: Event) {
    if (!this.allowOutsideClick) return;

    // Only close if clicking the overlay itself, not child elements
    if (e.target === e.currentTarget) {
      this.handleCancel();
    }
  }

  // ─── Public Actions ───────────────────────────────────────────────────────

  /**
   * Approve/confirm the modal.
   * Override this method to add custom validation before approval.
   */
  protected handleApprove() {
    approveModal();
  }

  /**
   * Cancel/close the modal.
   * Override this method to add custom cleanup on cancel.
   */
  protected handleCancel() {
    cancelModal();
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  render() {
    if (!this._isOpen) return html``;

    return html`
      <div
        class="modal-overlay fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        @click=${this._handleOverlayClick}
      >
        <div
          class="modal-dialog bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto relative"
          @click=${(e: Event) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          ${this.showCloseButton
            ? html`
                <button
                  class="modal-close absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl leading-none w-8 h-8 flex items-center justify-center"
                  @click=${this.handleCancel}
                  aria-label="Close modal"
                  type="button"
                >
                  ×
                </button>
              `
            : ""}

          <div class="modal-content p-6">
            <slot></slot>
          </div>
        </div>
      </div>
    `;
  }
}
